// src/features/blog/components/BlogFilter.jsx
import React, { useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Button,
  Popover,
  Grid,
  Typography,
} from "@mui/material";
import { Search, FilterList, Clear, Close } from "@mui/icons-material";
import { BLOG_STATUS_LABELS, BLOG_CATEGORIES } from "../types/blogTypes";

const BlogFilter = ({ filters = {}, onFilterChange, onClear }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [localFilters, setLocalFilters] = useState({
    status: filters.status || "all",
    category: filters.category || "all",
    search: filters.search || "",
    dateRange: filters.dateRange || { start: "", end: "" },
    authorId: filters.authorId || "all",
    tags: filters.tags || [],
  });

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterApply = () => {
    onFilterChange && onFilterChange(localFilters);
    handleFilterClose();
  };

  const handleFilterReset = () => {
    const emptyFilters = {
      status: "all",
      category: "all",
      search: "",
      dateRange: { start: "", end: "" },
      authorId: "all",
      tags: [],
    };
    setLocalFilters(emptyFilters);
    onFilterChange && onFilterChange(emptyFilters);
    handleFilterClose();
  };

  const handleChange = (field, value) => {
    setLocalFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (field, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      dateRange: { ...prev.dateRange, [field]: value },
    }));
  };

  const hasActiveFilters = () => {
    const { status, category, search, authorId, tags, dateRange } =
      localFilters;
    return (
      status !== "all" ||
      category !== "all" ||
      search ||
      authorId !== "all" ||
      tags.length > 0 ||
      dateRange?.start ||
      dateRange?.end
    );
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.status !== "all") count++;
    if (localFilters.category !== "all") count++;
    if (localFilters.search) count++;
    if (localFilters.authorId !== "all") count++;
    if (localFilters.tags.length > 0) count++;
    if (localFilters.dateRange?.start) count++;
    if (localFilters.dateRange?.end) count++;
    return count;
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        mb: 2,
        alignItems: "center",
      }}
    >
      <TextField
        size="small"
        placeholder="Search blog posts..."
        value={localFilters.search || ""}
        onChange={(e) => {
          handleChange("search", e.target.value);
          onFilterChange &&
            onFilterChange({ ...localFilters, search: e.target.value });
        }}
        sx={{ flex: 1, minWidth: 200 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
          endAdornment: localFilters.search && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  handleChange("search", "");
                  onFilterChange &&
                    onFilterChange({ ...localFilters, search: "" });
                }}
              >
                <Clear fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={localFilters.status || "all"}
          onChange={(e) => {
            handleChange("status", e.target.value);
            onFilterChange &&
              onFilterChange({ ...localFilters, status: e.target.value });
          }}
          label="Status"
        >
          <MenuItem value="all">All Status</MenuItem>
          {Object.entries(BLOG_STATUS_LABELS).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Category</InputLabel>
        <Select
          value={localFilters.category || "all"}
          onChange={(e) => {
            handleChange("category", e.target.value);
            onFilterChange &&
              onFilterChange({ ...localFilters, category: e.target.value });
          }}
          label="Category"
        >
          <MenuItem value="all">All Categories</MenuItem>
          {BLOG_CATEGORIES.map((cat) => (
            <MenuItem key={cat.value} value={cat.value}>
              {cat.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant={hasActiveFilters() ? "contained" : "outlined"}
        startIcon={<FilterList />}
        onClick={handleFilterClick}
        size="small"
        sx={{
          background: hasActiveFilters()
            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            : "none",
        }}
      >
        Filters
        {hasActiveFilters() && (
          <Chip
            label={getActiveFilterCount()}
            size="small"
            sx={{
              ml: 1,
              bgcolor: "white",
              color: "#667eea",
              height: 20,
              fontSize: "11px",
            }}
          />
        )}
      </Button>

      {hasActiveFilters() && (
        <Button
          size="small"
          startIcon={<Clear />}
          onClick={() => {
            const emptyFilters = {
              status: "all",
              category: "all",
              search: "",
              dateRange: { start: "", end: "" },
              authorId: "all",
              tags: [],
            };
            setLocalFilters(emptyFilters);
            onClear && onClear();
          }}
        >
          Clear All
        </Button>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { p: 3, width: 420, maxWidth: "90vw" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Filter Posts</Typography>
          <IconButton size="small" onClick={handleFilterClose}>
            <Close />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={localFilters.status || "all"}
                onChange={(e) => handleChange("status", e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                {Object.entries(BLOG_STATUS_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={localFilters.category || "all"}
                onChange={(e) => handleChange("category", e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {BLOG_CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Start Date"
              type="date"
              value={localFilters.dateRange?.start || ""}
              onChange={(e) => handleDateChange("start", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="End Date"
              type="date"
              value={localFilters.dateRange?.end || ""}
              onChange={(e) => handleDateChange("end", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Author ID"
              value={localFilters.authorId || ""}
              onChange={(e) => handleChange("authorId", e.target.value)}
              placeholder="Enter author ID"
            />
          </Grid>
        </Grid>

        <Box
          sx={{ display: "flex", gap: 1, mt: 3, justifyContent: "flex-end" }}
        >
          <Button variant="outlined" onClick={handleFilterReset}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleFilterApply}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </Popover>

      {/* Active filter chips */}
      {hasActiveFilters() && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", ml: 1 }}>
          {localFilters.status !== "all" && (
            <Chip
              label={`Status: ${BLOG_STATUS_LABELS[localFilters.status]}`}
              onDelete={() => {
                handleChange("status", "all");
                onFilterChange &&
                  onFilterChange({ ...localFilters, status: "all" });
              }}
              size="small"
            />
          )}
          {localFilters.category !== "all" && (
            <Chip
              label={`Category: ${BLOG_CATEGORIES.find((c) => c.value === localFilters.category)?.label || localFilters.category}`}
              onDelete={() => {
                handleChange("category", "all");
                onFilterChange &&
                  onFilterChange({ ...localFilters, category: "all" });
              }}
              size="small"
            />
          )}
          {localFilters.search && (
            <Chip
              label={`Search: ${localFilters.search}`}
              onDelete={() => {
                handleChange("search", "");
                onFilterChange &&
                  onFilterChange({ ...localFilters, search: "" });
              }}
              size="small"
            />
          )}
          {localFilters.authorId !== "all" && (
            <Chip
              label={`Author: ${localFilters.authorId}`}
              onDelete={() => {
                handleChange("authorId", "all");
                onFilterChange &&
                  onFilterChange({ ...localFilters, authorId: "all" });
              }}
              size="small"
            />
          )}
          {localFilters.tags.length > 0 && (
            <Chip
              label={`Tags: ${localFilters.tags.join(", ")}`}
              onDelete={() => {
                handleChange("tags", []);
                onFilterChange && onFilterChange({ ...localFilters, tags: [] });
              }}
              size="small"
            />
          )}
          {localFilters.dateRange?.start && (
            <Chip
              label={`From: ${localFilters.dateRange.start}`}
              onDelete={() => {
                handleDateChange("start", "");
                onFilterChange &&
                  onFilterChange({
                    ...localFilters,
                    dateRange: { ...localFilters.dateRange, start: "" },
                  });
              }}
              size="small"
            />
          )}
          {localFilters.dateRange?.end && (
            <Chip
              label={`To: ${localFilters.dateRange.end}`}
              onDelete={() => {
                handleDateChange("end", "");
                onFilterChange &&
                  onFilterChange({
                    ...localFilters,
                    dateRange: { ...localFilters.dateRange, end: "" },
                  });
              }}
              size="small"
            />
          )}
        </Stack>
      )}
    </Box>
  );
};

export default BlogFilter;
